import { ChangeEvent, TextareaHTMLAttributes, useState } from 'react'

interface Props
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'value' | 'onChange'> {
  /** 現在の値（1行 = 1要素） */
  lines: string[]
  /** 解析後の配列を返す */
  onChange(lines: string[]): void
  /** 上限件数。超えた分は切り捨てる */
  max?: number
  /** 各行の前後の空白を落とすか（既定 true） */
  trim?: boolean
}

/**
 * 「1行に1つ」を配列として扱う textarea。
 *
 * 素朴に `value={lines.join('\n')}` ＋ `onChange` で
 * `split('\n').map(trim).filter(Boolean)` とすると、**入力の末尾で改行できない**。
 * 末尾で Enter を押すと `'あいう\n'` → `['あいう', '']` → 空要素が落ちて
 * `['あいう']` になり、再描画で改行が消えるため。行の途中なら両側に文字が残るので
 * 改行でき、「途中ならできるが末尾はできない」という症状になる
 * （SMART_HUB_DEV-85 / 86）。
 *
 * 編集中の生テキストを自前で持ち、親へは解析した配列だけを渡す。
 * 保存される値はこれまでと同じで、編集中だけ空行・行末の空白が保たれる。
 */
const LinesTextarea = ({ lines, onChange, max, trim = true, rows = 4, ...rest }: Props) => {
  const parse = (raw: string) => {
    let parsed = raw.split('\n')
    if (trim) parsed = parsed.map((s) => s.trim())
    parsed = parsed.filter(Boolean)
    return max ? parsed.slice(0, max) : parsed
  }

  const fromProps = (lines || []).join('\n')
  // 編集中の生テキスト。親の配列から毎回作り直すと改行が消えるため、ここで保持する
  const [text, setText] = useState(fromProps)
  const [lastProps, setLastProps] = useState(fromProps)

  // 親の値が変わったときだけ見直す（描画中の state 更新。効果より取りこぼしが無い）。
  // **自分の編集が親を回って戻ってきただけなら、生テキストは保つ。**
  // ここで毎回上書きすると、末尾の改行が往復のたびに消える
  if (fromProps !== lastProps) {
    setLastProps(fromProps)
    if (parse(text).join('\n') !== fromProps) setText(fromProps)
  }

  const handle = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const raw = e.target.value
    setText(raw)
    onChange(parse(raw))
  }

  return <textarea rows={rows} value={text} onChange={handle} {...rest} />
}

export default LinesTextarea
